import type { JSX, ChangeEvent } from 'react';
import { useId } from 'react';
import { Input } from './ui';
import type { CharityType } from '../types';

export interface CharityInputProps {
  /**
   * Current charity donation amount
   */
  value: number;

  /**
   * Change handler for donation amount
   */
  onChange: (value: number) => void;

  /**
   * Optional label (defaults to "Charitable Donations")
   */
  label?: string;

  /**
   * Current charity donation type
   */
  charityType?: CharityType;

  /**
   * Handler for charity type changes
   */
  onCharityTypeChange?: (type: CharityType) => void;
}

const CHARITY_TYPE_LABELS: Record<CharityType, string> = {
  gift_aid: 'Gift Aid',
  payroll_giving: 'Payroll Giving',
};

const CHARITY_TYPE_DESCRIPTIONS: Record<CharityType, string> = {
  gift_aid: 'You donate after tax, charity claims 25% extra from HMRC',
  payroll_giving: 'Deducted before tax, full amount goes to charity',
};

/**
 * CharityInput component - handles charitable donations with type selection
 *
 * Features:
 * - Select donation type (Gift Aid or Payroll Giving)
 * - Helper text explaining each donation method
 * - Amount input with currency prefix
 *
 * @example
 * ```tsx
 * <CharityInput
 *   value={1000}
 *   onChange={(value) => setCharity(value)}
 *   charityType="gift_aid"
 *   onCharityTypeChange={(type) => setCharityType(type)}
 * />
 * ```
 */
export function CharityInput({
  value,
  onChange,
  label = 'Charitable Donations',
  charityType = 'gift_aid',
  onCharityTypeChange,
}: CharityInputProps): JSX.Element {
  const charityTypeId = useId();
  const amountInputId = useId();

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const numericValue = parseFloat(e.target.value) || 0;
    onChange(Math.round(numericValue));
  };

  const handleTypeChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    const newType = e.target.value as CharityType;
    onCharityTypeChange?.(newType);
  };

  const helperText = CHARITY_TYPE_DESCRIPTIONS[charityType];

  return (
    <div className="space-y-3">
      {/* Charity Type Selector */}
      {onCharityTypeChange && (
        <div>
          <label htmlFor={charityTypeId} className="block text-sm font-medium text-gray-700 mb-1">
            Donation Type
          </label>
          <select
            id={charityTypeId}
            value={charityType}
            onChange={handleTypeChange}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-2 focus:outline-primary focus:outline-offset-0"
          >
            {(Object.keys(CHARITY_TYPE_LABELS) as CharityType[]).map((type) => (
              <option key={type} value={type}>
                {CHARITY_TYPE_LABELS[type]}
              </option>
            ))}
          </select>
          <p className="mt-1 text-sm text-gray-500">{helperText}</p>
        </div>
      )}

      {/* Amount Input */}
      <div>
        <label htmlFor={amountInputId} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <Input
          id={amountInputId}
          type="number"
          value={value}
          onChange={handleInputChange}
          prefix="£"
          fullWidth
          selectAllOnFocus
        />
      </div>
    </div>
  );
}
